import { useState, useCallback, useEffect } from 'react';
import { ClientAnalytics, AgentAnalytics, UseBusinessAnalyticsReturn } from '../types/business';
import { BusinessAnalytics } from '../utils/BusinessAnalytics';

interface UseBusinessAnalyticsOptions {
  businessId?: string;
  userEmail?: string;
  userType?: 'agent' | 'admin';
}

export function useBusinessAnalytics({ businessId, userEmail }: UseBusinessAnalyticsOptions = {}): UseBusinessAnalyticsReturn {
  // Loading states
  const [isLoadingClientAnalytics, setIsLoadingClientAnalytics] = useState<boolean>(false);
  const [isLoadingAgentAnalytics, setIsLoadingAgentAnalytics] = useState<boolean>(false);
  
  // Error states
  const [clientAnalyticsError, setClientAnalyticsError] = useState<string | null>(null);
  const [agentAnalyticsError, setAgentAnalyticsError] = useState<string | null>(null);

  // Clean up expired cache on mount
  useEffect(() => {
    BusinessAnalytics.cleanupExpiredCache();
  }, []);

  /**
   * Fetch client analytics from API
   */
  const fetchClientAnalyticsFromAPI = useCallback(async (
    clientEmail: string, 
    homeId?: string, 
    daysBack: number = 7
  ): Promise<ClientAnalytics | null> => {
    if (!businessId || !userEmail) {
      throw new Error('Business ID and user email are required');
    }

    // Choose endpoint based on whether homeId is provided
    const params = new URLSearchParams();
    if (daysBack !== 7) {
      params.append('daysBack', daysBack.toString());
    }

    let url: string;
    if (homeId) {
      // Use home-specific endpoint
      const queryString = params.toString();
      url = `/api/business/${businessId}/${encodeURIComponent(userEmail)}/client/${encodeURIComponent(clientEmail)}/home/${encodeURIComponent(homeId)}/analytics${queryString ? `?${queryString}` : ''}`;
    } else {
      // Use general client analytics endpoint
      const queryString = params.toString();
      url = `/api/business/${businessId}/${encodeURIComponent(userEmail)}/client/${encodeURIComponent(clientEmail)}/analytics${queryString ? `?${queryString}` : ''}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      credentials: 'same-origin'
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error || 'Failed to fetch client analytics');
    }

    // Transform the API response to match our ClientAnalytics interface
    // The API returns imageEngagement, areaEngagement, homeOpens, and dailyEngagement data
    const imageEngagementData = data.analytics.imageEngagement;
    const areaEngagementData = data.analytics.areaEngagement;
    const homeOpensData = data.analytics.homeOpens;
    const dailyEngagementData = data.analytics.dailyEngagement;
    
    if (!imageEngagementData && !areaEngagementData && !homeOpensData) {
      throw new Error('No analytics data in response');
    }

    // Transform the backend response to our frontend interface
    const analytics: ClientAnalytics = {
      imageEngagement: imageEngagementData?.topEngagedImages?.map((item: any) => {
        // Debug logging to see what we're getting from the backend
        console.log('Image engagement item from backend:', item);
        
        // Validate that we have the imageUrl field
        if (!item.imageUrl) {
          console.warn('Missing imageUrl field in backend response:', item);
        }
        
        return {
          homeId: item.homeId,
          imageUrl: item.imageUrl || '', // Now using the actual imageUrl field from backend
          engagementTimeMs: item.engagementTimeMs,
          searchStartDate: imageEngagementData?.dateRange?.start || '',
          searchEndDate: imageEngagementData?.dateRange?.end || ''
        };
      }) || [],
      areaEngagement: areaEngagementData?.topEngagedAreas?.map((item: any) => {
        // Debug logging to see what we're getting from the backend
        console.log('Area engagement item from backend:', item);
        
        return {
          areaName: item.areaName,
          homeId: item.homeId,
          engagementTimeMs: item.engagementTimeMs,
          viewCount: item.viewCount,
          dismissalReasons: item.dismissalReasons || []
        };
      }) || [],
      homeOpens: homeOpensData?.homeOpens?.map((item: any) => ({
        homeId: item.homeId,
        totalOpens: item.totalOpens,
        previousTotalOpens: item.previousTotalOpens || 0
      })) || [],
      dailyEngagement: dailyEngagementData?.dailyEngagement?.map((item: any) => ({
        homeId: item.homeId,
        eventDate: item.eventDate,
        engagementTimeMs: item.engagementTimeMs
      })) || [],
      summary: {
        totalEngagementTimeMs: imageEngagementData?.summary?.totalEngagementTimeMs || 0,
        uniqueImages: imageEngagementData?.summary?.totalImagesViewed || 0,
        totalHomeOpens: homeOpensData?.summary?.totalHomeOpens || 0,
        totalHomesOpened: homeOpensData?.summary?.totalHomesOpened || 0,
        previousTotalHomeOpens: homeOpensData?.summary?.previousTotalHomeOpens || 0,
        totalAreaEngagementTimeMs: areaEngagementData?.summary?.totalAreaEngagementTimeMs || 0,
        totalAreasEngaged: areaEngagementData?.summary?.totalAreasEngaged || 0,
        totalAreaViews: areaEngagementData?.summary?.totalAreaViews || 0,
        averageEngagementPerArea: areaEngagementData?.summary?.averageEngagementPerArea || 0,
        totalDaysEngaged: dailyEngagementData?.summary?.totalDaysEngaged || 0,
        totalEngagementTimeAllDays: dailyEngagementData?.summary?.totalEngagementTimeAllDays || 0,
        averageEngagementPerDay: dailyEngagementData?.summary?.averageEngagementPerDay || 0,
        dateRange: {
          start: imageEngagementData?.dateRange?.start || areaEngagementData?.dateRange?.start || homeOpensData?.dateRange?.start || new Date(Date.now() - daysBack * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          end: imageEngagementData?.dateRange?.end || areaEngagementData?.dateRange?.end || homeOpensData?.dateRange?.end || new Date().toISOString().split('T')[0]
        }
      }
    };

    return analytics;
  }, [businessId, userEmail]);

  /**
   * Fetch agent analytics from API
   */
  const fetchAgentAnalyticsFromAPI = useCallback(async (
    agentEmail: string, 
    timeframe: string = '7d'
  ): Promise<AgentAnalytics | null> => {
    if (!businessId) {
      throw new Error('Business ID is required');
    }

    const url = `/api/business/${businessId}/${encodeURIComponent(agentEmail)}/analytics?timeframe=${timeframe}`;

    const response = await fetch(url, {
      method: 'GET',
      credentials: 'same-origin'
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error || 'Failed to fetch agent analytics');
    }

    return data.analytics;
  }, [businessId]);

  /**
   * Get client analytics with caching
   */
  const getClientAnalytics = useCallback(async (
    clientEmail: string, 
    homeId?: string, 
    daysBack: number = 7
  ): Promise<ClientAnalytics | null> => {
    if (!businessId) {
      console.warn('Business ID is required for client analytics');
      return null;
    }

    setClientAnalyticsError(null);

    // Check cache first
    const cacheParams = homeId ? { homeId, daysBack } : { daysBack };
    const cached = BusinessAnalytics.getCachedData<ClientAnalytics>(
      businessId, 
      'client', 
      clientEmail, 
      cacheParams
    );

    if (cached) {
      return cached;
    }

    // Fetch from API if not in cache or expired
    setIsLoadingClientAnalytics(true);
    
    try {
      const analytics = await fetchClientAnalyticsFromAPI(clientEmail, homeId, daysBack);
      
      if (analytics) {
        // Cache the result
        BusinessAnalytics.setCachedData(
          businessId, 
          'client', 
          clientEmail, 
          analytics, 
          cacheParams
        );
      }
      
      return analytics;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch client analytics';
      setClientAnalyticsError(errorMessage);
      console.error('Error fetching client analytics:', error);
      return null;
    } finally {
      setIsLoadingClientAnalytics(false);
    }
  }, [businessId, fetchClientAnalyticsFromAPI]);

  /**
   * Get agent analytics with caching
   */
  const getAgentAnalytics = useCallback(async (
    agentEmail: string, 
    timeframe: string = '7d'
  ): Promise<AgentAnalytics | null> => {
    if (!businessId) {
      console.warn('Business ID is required for agent analytics');
      return null;
    }

    setAgentAnalyticsError(null);

    // Check cache first
    const cacheParams = { timeframe };
    const cached = BusinessAnalytics.getCachedData<AgentAnalytics>(
      businessId, 
      'agent', 
      agentEmail, 
      cacheParams
    );

    if (cached) {
      return cached;
    }

    // Fetch from API if not in cache or expired
    setIsLoadingAgentAnalytics(true);
    
    try {
      const analytics = await fetchAgentAnalyticsFromAPI(agentEmail, timeframe);
      
      if (analytics) {
        // Cache the result
        BusinessAnalytics.setCachedData(
          businessId, 
          'agent', 
          agentEmail, 
          analytics, 
          cacheParams
        );
      }
      
      return analytics;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch agent analytics';
      setAgentAnalyticsError(errorMessage);
      console.error('Error fetching agent analytics:', error);
      return null;
    } finally {
      setIsLoadingAgentAnalytics(false);
    }
  }, [businessId, fetchAgentAnalyticsFromAPI]);

  /**
   * Clear all cache
   */
  const clearCache = useCallback(() => {
    if (businessId) {
      BusinessAnalytics.clearAllCache(businessId);
    }
  }, [businessId]);

  /**
   * Refresh specific cache entry
   */
  const refreshCache = useCallback(async (type: 'client' | 'agent', identifier: string, homeId?: string) => {
    if (!businessId) return;
    
    if (type === 'client') {
      // Clear client cache and refetch
      const cacheParams = homeId ? { homeId, daysBack: 7 } : { daysBack: 7 };
      BusinessAnalytics.clearCache(businessId, 'client', identifier, cacheParams);
      await getClientAnalytics(identifier, homeId);
    } else {
      // Clear agent cache and refetch
      BusinessAnalytics.clearCache(businessId, 'agent', identifier);
      await getAgentAnalytics(identifier);
    }
  }, [businessId, getClientAnalytics, getAgentAnalytics]);

  /**
   * Get cache info for specific entry
   */
  const getCacheInfo = useCallback((type: 'client' | 'agent', identifier: string, homeId?: string) => {
    if (!businessId) {
      return { isStale: true, lastFetched: null };
    }
    
    const cacheParams = homeId ? { homeId, daysBack: 7 } : { daysBack: 7 };
    return BusinessAnalytics.getCacheInfo(businessId, type, identifier, cacheParams);
  }, [businessId]);

  return {
    // Client Analytics
    getClientAnalytics,
    isLoadingClientAnalytics,
    clientAnalyticsError,
    
    // Agent Analytics
    getAgentAnalytics,
    isLoadingAgentAnalytics,
    agentAnalyticsError,
    
    // Cache Management
    clearCache,
    refreshCache,
    getCacheInfo
  };
}